import { useQuery } from '@tanstack/react-query';
import { apiSearch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';
import type { ClientPage } from './types.ts';

/**
 * How many the quick search offers: it is for jumping to one, not browsing.
 * More clients are asked for than are shown, so the ones on the page in front
 * of you can be put first: a surname shared by eight clients should still find
 * the one on screen.
 */
export const QUICK_CLIENTS_FETCHED = 20;
export const QUICK_CLIENTS_SHOWN = 8;
export const QUICK_PLACES = 4;
/** One letter finds every client with that letter somewhere; places wait for two. */
export const PLACE_MIN_CHARS = 2;

/** `GET /places`: a place where some of the caller's clients are now. */
export interface PlaceMatch {
  /** The facility board's key for the card. */
  location_key: string;
  location: string;
  location_address: string | null;
  county: string | null;
  /** The caller's clients there, and the first twelve of them by name. */
  client_count: number;
  clients: { client_id: string; display_name: string; client_ref: string }[];
}

/**
 * Clients, over the same `POST /clients/search` the client list and the top bar use,
 * which matches names, references, aliases and former names.
 */
export function useQuickClientSearch(q: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['clients', 'quick', q],
    queryFn: () =>
      apiSearch<ClientPage>('/clients/search', { q, sort: 'last_name', offset: 0, limit: QUICK_CLIENTS_FETCHED }, getToken),
    ...queryDefaults,
    enabled: q.length > 0,
    placeholderData: (previous) => previous,
  });
}

/**
 * Homes by name or street, where the caller's clients live (13 October): the
 * top bar's homes, over the same `POST /places`, homes only, since the top bar
 * finds buildings over `POST /facilities/search`. Row security narrows it to the
 * caller's own clients, as it does the tiles.
 */
export function useHomeSearch(q: string, limit: number) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['places', 'homes', q, limit],
    queryFn: () =>
      apiSearch<{ places: PlaceMatch[] }>('/places', { q, limit, homes: 'true' }, getToken),
    ...queryDefaults,
    enabled: q.length >= PLACE_MIN_CHARS,
    placeholderData: (previous) => previous,
  });
}

/** Places by name, where the caller's clients are now. */
export function useQuickPlaceSearch(q: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['places', 'quick', q],
    queryFn: () =>
      apiSearch<{ places: PlaceMatch[] }>('/places', { q, limit: QUICK_PLACES }, getToken),
    ...queryDefaults,
    enabled: q.length >= PLACE_MIN_CHARS,
    placeholderData: (previous) => previous,
  });
}
